import { useCookieConsent } from "~context/cookieConsentContext";
import { trackPurchaseFB, trackLeadFB, pageviewFB } from "~lib/helpers/fbPixel";
import {
  seekaTrackRegistration,
  seekaTrackWaitlistSignup,
  seekaTrackSuccessfullOrder,
  seekaTrackCheckout,
  seekaTrackMembershipAddToCart,
} from "~lib/seeka";
import ReactGA from "react-ga4";
import posthogJS from "posthog-js";
import type {
  OrderQuery,
  CurrentOrganizationQuery,
  JoinWaitlistMutation,
  JoinResaleWaitlistMutation,
  GetOrderJobStatusQuery,
  MembershipQuery,
} from "~graphql/typed-document-nodes";
import type { WaitListFormValues } from "~components/events/waitlist/interface";
import type { ConvergeSdk } from "@seeka-labs/converge";
import type { SeatsIOSeat } from "~components/reservation/constants";
import useIsGAInitialised from "~features/tracking/analytics/useIsGAInitialised";
import type { Properties } from "posthog-js";

// ---- FACEBOOK PIXEL TRACKING ----
export const trackPurchaseFBWithConsent = (
  order: OrderQuery["order"],
  organization: CurrentOrganizationQuery["currentOrganization"],
  pixelId?: string
) => {
  const { isMarketingAllowed } = useCookieConsent();
  if (isMarketingAllowed() && pixelId) {
    trackPurchaseFB(order, organization, pixelId);
  }
};

export const trackLeadFBWithConsent = (
  values: WaitListFormValues,
  eventName: string,
  organization: CurrentOrganizationQuery["currentOrganization"],
  pixelId?: string
) => {
  const { isMarketingAllowed } = useCookieConsent();
  if (isMarketingAllowed() && pixelId) {
    trackLeadFB(values, eventName, organization, pixelId);
  }
};

export const pageviewFBWithConsent = (pixelId?: string) => {
  const { isMarketingAllowed } = useCookieConsent();
  if (isMarketingAllowed() && pixelId) {
    pageviewFB(pixelId);
  }
};

// ---- GOOGLE ANALYTICS TRACKING ----
export const trackGAEventWithConsent = (eventName: string, params?: any) => {
  const { isAnalyticsAllowed } = useCookieConsent();
  const isGAInitialised = useIsGAInitialised();

  if (isAnalyticsAllowed() && isGAInitialised) {
    ReactGA.event(eventName, params);
  }
};

export const trackGAPageviewWithConsent = (path: string) => {
  const { isAnalyticsAllowed } = useCookieConsent();
  const isGAInitialised = useIsGAInitialised();

  if (isAnalyticsAllowed() && isGAInitialised) {
    ReactGA.send({ hitType: "pageview", page: path });
  }
};

// ---- POSTHOG TRACKING ----
export const trackPosthogEventWithConsent = (
  eventName: string,
  properties?: Properties
) => {
  const { isAnalyticsAllowed } = useCookieConsent();
  if (isAnalyticsAllowed()) {
    posthogJS.capture(eventName, properties);
  }
};

// ---- SEEKA TRACKING ----
export const trackSeekaRegistrationWithConsent = (
  converge: ConvergeSdk | undefined,
  values: WaitListFormValues,
  orgCountryCode?: string,
  joinWaitlistResult?: JoinWaitlistMutation["joinWaitlist"]
): void => {
  const { isMarketingAllowed } = useCookieConsent();
  if (isMarketingAllowed()) {
    seekaTrackRegistration(
      converge,
      values,
      orgCountryCode,
      joinWaitlistResult
    );
  }
};

export const trackSeekaWaitlistSignupWithConsent = (
  converge: ConvergeSdk | undefined,
  event: { id: string; name: string },
  values: WaitListFormValues,
  ticketTypes: { id: string; name: string; quantity?: number }[],
  orgCountryCode: string,
  joinResaleWaitlistResult?: JoinResaleWaitlistMutation["joinResaleWaitlist"]
): void => {
  const { isMarketingAllowed } = useCookieConsent();
  if (isMarketingAllowed()) {
    seekaTrackWaitlistSignup(
      converge,
      event,
      values,
      ticketTypes,
      orgCountryCode,
      joinResaleWaitlistResult
    );
  }
};

export const trackSeekaOrderWithConsent = (
  converge: ConvergeSdk | undefined,
  order: OrderQuery["order"]
): void => {
  const { isMarketingAllowed } = useCookieConsent();
  if (isMarketingAllowed()) {
    seekaTrackSuccessfullOrder(converge, order);
  }
};

export const trackSeekaCheckoutWithConsent = (
  converge: ConvergeSdk | undefined,
  order: GetOrderJobStatusQuery["getOrderJobStatus"]["order"],
  currencyCode: string
): void => {
  const { isMarketingAllowed } = useCookieConsent();
  if (isMarketingAllowed()) {
    seekaTrackCheckout(converge, order, currencyCode);
  }
};

export const trackSeekaMembershipAddToCartWithConsent = async (
  converge: ConvergeSdk | undefined,
  membership: MembershipQuery["membership"],
  seats: SeatsIOSeat[],
  getPrice: (seat: SeatsIOSeat) => [number, number]
): Promise<void> => {
  const { isMarketingAllowed } = useCookieConsent();
  if (isMarketingAllowed()) {
    await seekaTrackMembershipAddToCart(converge, membership, seats, getPrice);
  }
};
